/**
 * Staff view of a thread. Whether a message reached the customer is the most
 * consequential fact here, so internal notes get amber ground, a lock and an
 * explicit label rather than a subtle tint.
 */

import { Lock } from 'lucide-react';
import { format } from 'date-fns';

import { cn } from '@/lib/utils';
import type { SupportMessage } from '@repo/types';

function timestamp(iso: string): string {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? '' : format(date, 'd MMM, HH:mm');
}

function SystemRow({ message }: { message: SupportMessage }) {
  return (
    <li className="flex items-center gap-3">
      <span className="h-px flex-1 bg-border" />
      <span className="whitespace-nowrap text-xs text-muted-foreground">
        {message.body} · {timestamp(message.createdAt)}
      </span>
      <span className="h-px flex-1 bg-border" />
    </li>
  );
}

function MessageRow({ message }: { message: SupportMessage }) {
  const fromStaff = message.authorType === 'employee';
  const internal = message.visibility === 'internal';

  return (
    <li className={cn('flex flex-col gap-1', fromStaff ? 'items-end' : 'items-start')}>
      <span className="flex items-center gap-1.5 px-1 text-xs text-muted-foreground">
        {internal && <Lock className="size-3" />}
        {message.authorName ?? (fromStaff ? 'Staff' : 'Customer')}
        {internal && <span className="font-medium text-amber-600">· internal note</span>}
        <span>· {timestamp(message.createdAt)}</span>
      </span>

      <div
        className={cn(
          'max-w-[80%] whitespace-pre-wrap break-words rounded-lg border px-3.5 py-2.5 text-sm leading-relaxed',
          internal
            ? 'border-amber-500/40 bg-amber-500/10 text-foreground'
            : fromStaff
              ? 'border-transparent bg-primary text-primary-foreground'
              : 'border-border bg-muted text-foreground'
        )}
      >
        {message.body}
      </div>
    </li>
  );
}

export function MessageThread({ messages }: { messages: SupportMessage[] }) {
  if (messages.length === 0) {
    return <p className="py-8 text-center text-sm text-muted-foreground">No messages yet.</p>;
  }

  return (
    <ul className="flex flex-col gap-4">
      {messages.map((message) =>
        message.authorType === 'system' ? (
          <SystemRow key={message.id} message={message} />
        ) : (
          <MessageRow key={message.id} message={message} />
        )
      )}
    </ul>
  );
}
